/**
 * Student Upload Dialog Component
 *
 * Modal dialog for uploading Excel file with student data.
 * Warns user that upload will replace ALL existing students.
 */

import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Box,
} from '@mui/material';
import { Upload as UploadIcon } from '@mui/icons-material';
import { useUploadStudentsMutation } from '../../../api/studentsApi';

interface StudentUploadDialogProps {
  open: boolean;
  onClose: () => void;
  studioId: string;
}

const StudentUploadDialog: React.FC<StudentUploadDialogProps> = ({
  open,
  onClose,
  studioId,
}) => {
  const [uploadStudents, { isLoading }] = useUploadStudentsMutation();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      const validTypes = [
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.oasis.opendocument.spreadsheet',
      ];
      if (
        !validTypes.includes(file.type) &&
        !file.name.match(/\.(xlsx?|ods)$/i)
      ) {
        setError('Please select a valid Excel file (.xls, .xlsx, or .ods)');
        setSelectedFile(null);
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError('File size must be less than 5MB');
        setSelectedFile(null);
        return;
      }

      setSelectedFile(file);
      setError(null);
      setSuccess(null);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    try {
      const result = await uploadStudents({
        studioId,
        file: selectedFile,
      }).unwrap();
      setSuccess(
        `Successfully uploaded! Deleted ${result.deleted_count} students, created ${result.created_count} students.`
      );
      setSelectedFile(null);

      // Close dialog after short delay to show success message
      setTimeout(() => {
        onClose();
        setSuccess(null);
      }, 2000);
    } catch (err: any) {
      setError(
        err?.data?.message || 'Failed to upload file. Please try again.'
      );
    }
  };

  const handleClose = () => {
    setSelectedFile(null);
    setError(null);
    setSuccess(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Upload Student Roster</DialogTitle>
      <DialogContent>
        <Alert severity="warning" sx={{ mb: 2 }}>
          <strong>Warning:</strong> Uploading a file will DELETE all existing
          students and replace them with the data from the file.
        </Alert>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        <Box>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Select an Excel file (.xls, .xlsx, or .ods) with student data. The
            file should include columns for: Location, First Name, Last Name,
            Phone, Gender, Age, Birthday, and Classes.
          </Typography>

          <Button
            variant="outlined"
            component="label"
            startIcon={<UploadIcon />}
            fullWidth
            disabled={isLoading}
          >
            Choose File
            <input
              type="file"
              hidden
              accept=".xls,.xlsx,.ods"
              onChange={handleFileChange}
            />
          </Button>

          {selectedFile && (
            <Typography variant="body2" sx={{ mt: 1 }}>
              Selected: {selectedFile.name}
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          onClick={handleUpload}
          variant="contained"
          disabled={isLoading || !selectedFile}
        >
          {isLoading ? <CircularProgress size={24} /> : 'Upload'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default StudentUploadDialog;
